import { createIsomorphicFn } from "@tanstack/react-start";
import yn from "yn";

export interface PublicEnv {
  GLITCHTIP_DSN?: string;
  SENTRY_ENABLED: boolean;
  OPENPANEL_ENABLED: boolean;
  APP_VERSION?: string;
  ASPNETCORE_ENVIRONMENT: string;
}

declare global {
  interface Window {
    clientEnv?: PublicEnv;
  }
}

const FALLBACK: PublicEnv = {
  SENTRY_ENABLED: false,
  OPENPANEL_ENABLED: false,
  ASPNETCORE_ENVIRONMENT: "development",
};

/**
 * Public (non-secret) env the browser needs, resolved at request time rather than inlined at build
 * time: the portal image is built once in CI and the same bundle runs in every environment, so
 * SENTRY_ENABLED / ASPNETCORE_ENVIRONMENT / APP_VERSION are only known per container. The root shell
 * serializes this into `window.clientEnv` (see routes/__root.tsx), and the browser reads it back —
 * mirrors apps/portal's root-loader `clientEnv`.
 *
 * Read via a classic inline script so it is set during HTML parse, i.e. before the deferred client
 * module runs `getRouter()` and inits Sentry.
 */
export const getPublicEnv = createIsomorphicFn()
  .server((): PublicEnv => {
    // Lowercased to match the backend's GlitchTip environment (which lowercases EnvironmentName) —
    // GlitchTip treats "Production" and "production" as separate environments.
    const environment = (process.env.ASPNETCORE_ENVIRONMENT || "development").toLowerCase();
    return {
      GLITCHTIP_DSN: process.env.GLITCHTIP_DSN,
      SENTRY_ENABLED:
        process.env.NODE_ENV === "production" || yn(process.env.SENTRY_ENABLED, { default: false }),
      OPENPANEL_ENABLED: yn(process.env.OPENPANEL_ENABLED, {
        default: environment === "production",
      }),
      APP_VERSION: process.env.APP_VERSION,
      ASPNETCORE_ENVIRONMENT: environment,
    };
  })
  .client((): PublicEnv => window.clientEnv ?? FALLBACK);
